/**
 * CR-7037: the page a printed ASN QR code opens.
 *
 *   /s/asn/:asnNumber                      header code → that ASN
 *   /s/asn/:asnNumber/l/:lineId            line code → the PO line behind it
 *   /s/asn/:asnNumber/s/:scheduleId        schedule-row code → its PO line
 *
 * The server decides where the scan goes (see useAsnScanResolver); this page
 * only shows progress, then forwards — or explains why it cannot, so a scan
 * never quietly lands on the ASN list. Logged-out scans reach here after
 * login with the address intact (CR-5887).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAsnScanResolver, type AsnScanBlockedReason } from '../hooks/useAsnScanResolver';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { AlertCircle, Clock, Loader2, Lock, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface BlockedState {
  reason: AsnScanBlockedReason;
  title: string;
  description: string;
}

const BLOCKED_ICON: Record<AsnScanBlockedReason, LucideIcon> = {
  not_submitted: Clock,
  no_access: Lock,
  error: AlertCircle,
};

function toId(value: string | undefined): number | undefined {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const id = parseInt(value, 10);
  return id > 0 ? id : undefined;
}

export default function AsnScan() {
  const { asnNumber = '', lineId, scheduleId } = useParams();
  const navigate = useNavigate();
  const resolveScan = useAsnScanResolver();
  const [blocked, setBlocked] = useState<BlockedState | null>(null);
  // Resolve each scanned address once: a site switch re-creates resolveScan,
  // and running it again would switch and navigate twice.
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const key = `${asnNumber}|${lineId ?? ''}|${scheduleId ?? ''}`;
    if (handledRef.current === key) return;
    handledRef.current = key;
    setBlocked(null);

    const line = toId(lineId);
    const schedule = toId(scheduleId);
    resolveScan({
      asn_number: asnNumber,
      ...(line !== undefined ? { line } : {}),
      ...(schedule !== undefined ? { schedule } : {}),
    }).then((result) => {
      if (result.kind === 'navigate') {
        navigate(result.to, { replace: true });
      } else if (result.kind === 'blocked') {
        setBlocked({ reason: result.reason, title: result.title, description: result.description });
      }
    });
  }, [asnNumber, lineId, scheduleId, resolveScan, navigate]);

  if (!blocked) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 min-h-[60vh] text-muted-foreground" data-testid="asn-scan-resolving">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
        <p>Opening ASN {asnNumber}…</p>
      </div>
    );
  }

  const Icon = BLOCKED_ICON[blocked.reason];
  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="asn-scan-blocked">
      <Card className="max-w-lg w-full">
        <CardContent className="flex flex-col items-center text-center gap-4 py-10">
          <div className="rounded-full bg-muted p-4">
            <Icon className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-semibold">{blocked.title}</h1>
          <p className="text-muted-foreground">{blocked.description}</p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Button asChild>
              <Link to="/purchasing?tab=asn">
                <Truck className="h-4 w-4 mr-2" />
                Go to ASNs
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
